import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CircleMarker, MapContainer, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { BroodRoomIcon } from '../components/hives/BroodRoomIcon';
import { QueenColorIcon } from '../components/hives/QueenColorIcon';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../lib/api';
import { apiariesApi, type Apiary } from '../services/apiaries';
import { hivesApi, type Hive, type HiveMetaItem } from '../services/hives';

type FormState = {
  name: string;
  address1: string;
  address2: string;
  postalCode: string;
  city: string;
  markagare: string;
  kontaktinfo: string;
  lat: string;
  lng: string;
  notes: string;
};

const emptyForm: FormState = {
  name: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  markagare: '',
  kontaktinfo: '',
  lat: '',
  lng: '',
  notes: '',
};

type ApiaryLogForm = {
  logDate: string;
  eventType: string;
  isTreatment: boolean;
  preparationProductId: string;
  preparation: string;
  withdrawalDays: string;
  notes: string;
};

function todayDateValue(): string {
  return new Date().toLocaleDateString('sv-SE');
}

function createEmptyApiaryLogForm(): ApiaryLogForm {
  return {
    logDate: todayDateValue(),
    eventType: '',
    isTreatment: false,
    preparationProductId: '',
    preparation: '',
    withdrawalDays: '',
    notes: '',
  };
}

const emptyApiaryLogForm: ApiaryLogForm = createEmptyApiaryLogForm();

const TREATMENT_LOG_TYPES = new Set(['Varroabehandling']);

function parseLatLng(v: string): number | null {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function apiaryToForm(apiary: Apiary | null): FormState {
  if (!apiary) return emptyForm;
  return {
    name: apiary.name ?? '',
    address1: apiary.address1 ?? '',
    address2: apiary.address2 ?? '',
    postalCode: apiary.postalCode ?? '',
    city: apiary.city ?? '',
    markagare: apiary.markagare ?? '',
    kontaktinfo: apiary.kontaktinfo ?? '',
    lat: apiary.lat ?? '',
    lng: apiary.lng ?? '',
    notes: apiary.notes ?? '',
  };
}

function textValue(value?: string | null): string {
  return value && value.trim() !== '' ? value : '-';
}

function isTreatmentLogType(eventType: string) {
  return TREATMENT_LOG_TYPES.has(eventType);
}

function splitStatusLabel(label: string | null | undefined): { short: string; long: string } {
  const raw = (label || '').trim();
  if (!raw) return { short: '-', long: '-' };
  const idx = raw.indexOf(' - ');
  if (idx > 0) {
    const short = raw.slice(0, idx).trim();
    const long = raw.slice(idx + 3).trim();
    return { short, long: long || short };
  }
  return { short: raw, long: raw };
}

function shortFormatLabel(label: string | null | undefined): string {
  const raw = (label || '').trim().toUpperCase();
  if (!raw) return '-';
  if (raw.includes('LN')) return 'LN';
  if (raw === 'D' || raw.includes('DADANT')) return 'D';
  if (raw === 'L' || raw.includes('LANG')) return 'L';
  return raw;
}

function isInQuarantine(withdrawalUntil?: string | null): boolean {
  if (!withdrawalUntil) return false;
  const until = new Date(withdrawalUntil.replace(' ', 'T'));
  if (Number.isNaN(until.getTime())) return false;
  return until.getTime() >= Date.now();
}

function MapClickSetter({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapViewUpdater({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  useEffect(() => {
    if (lat !== null && lng !== null) {
      map.setView([lat, lng], Math.max(map.getZoom(), 11));
    }
  }, [lat, lng, map]);
  return null;
}

function StaticMap({
  lat,
  lng,
  editable,
  onPick,
  height = 320,
}: {
  lat: number | null;
  lng: number | null;
  editable: boolean;
  onPick?: (lat: number, lng: number) => void;
  height?: number;
}) {
  const center: [number, number] = lat !== null && lng !== null ? [lat, lng] : [57.0, 15.0];

  return (
    <div className="overflow-hidden rounded border border-gray-300">
      <MapContainer center={center} zoom={lat !== null && lng !== null ? 11 : 6} style={{ height, width: '100%' }}>
        <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapViewUpdater lat={lat} lng={lng} />
        {editable && onPick ? <MapClickSetter onPick={onPick} /> : null}
        {lat !== null && lng !== null ? (
          <CircleMarker center={[lat, lng]} radius={8} pathOptions={{ color: '#f97316' }}>
            <Popup>
              {lat.toFixed(7)}, {lng.toFixed(7)}
            </Popup>
          </CircleMarker>
        ) : null}
      </MapContainer>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 text-sm">
      <div className="font-medium text-gray-700">{label}:</div>
      <div className="text-gray-900 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

export function ApiaryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { canWrite } = useAuth();
  const canWriteHives = canWrite('hives');
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [apiary, setApiary] = useState<Apiary | null>(null);
  const [hives, setHives] = useState<Hive[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [todoDraft, setTodoDraft] = useState('');
  const [apiaryLogForm, setApiaryLogForm] = useState<ApiaryLogForm>(emptyApiaryLogForm);
  const [savingApiaryLog, setSavingApiaryLog] = useState(false);
  const [savingTodo, setSavingTodo] = useState(false);
  const [showApiaryLogDialog, setShowApiaryLogDialog] = useState(false);
  const [hiveLogTypeItems, setHiveLogTypeItems] = useState<HiveMetaItem[]>([]);
  const [preparations, setPreparations] = useState<Array<{ id: number; sku: string | null; name: string; withdrawalDays: number | null; treatmentLengthDays: number | null; instructionUrl: string | null }>>([]);

  const formLat = useMemo(() => parseLatLng(form.lat), [form.lat]);
  const formLng = useMemo(() => parseLatLng(form.lng), [form.lng]);
  const viewLat = useMemo(() => parseLatLng(apiary?.lat ?? ''), [apiary?.lat]);
  const viewLng = useMemo(() => parseLatLng(apiary?.lng ?? ''), [apiary?.lng]);

  useEffect(() => {
    if (isNew) {
      setApiary(null);
      setHives([]);
      setForm(emptyForm);
      setEditing(true);
      setLoading(false);
      return;
    }

    const apiaryId = Number(id);
    if (!Number.isFinite(apiaryId) || apiaryId < 1) {
      toast.error('Ogiltigt bigårds-id');
      navigate('/apiaries', { replace: true });
      return;
    }

    let active = true;
    setLoading(true);
    void Promise.all([apiariesApi.get(apiaryId), hivesApi.list({ apiaryId })])
      .then(([detail, hiveRes]) => {
        if (!active) return;
        setApiary(detail);
        setForm(apiaryToForm(detail));
        setHives(hiveRes.hives);
        setEditing(false);
      })
      .catch((error) => {
        if (!active) return;
        toast.error(error instanceof Error ? error.message : 'Kunde inte ladda bigård');
        navigate('/apiaries', { replace: true });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id, isNew, navigate, toast]);

  useEffect(() => {
    setTodoDraft(apiary?.todoNotes ?? '');
  }, [apiary?.todoNotes]);

  async function reloadDetail(apiaryId: number) {
    const [detail, hiveRes] = await Promise.all([apiariesApi.get(apiaryId), hivesApi.list({ apiaryId })]);
    setApiary(detail);
    setForm(apiaryToForm(detail));
    setHives(hiveRes.hives);
  }

  async function handleSave() {
    if (!canWrite('apiaries') || saving) return;
    if (!form.name.trim()) {
      toast.error('Namn krävs');
      return;
    }

    const payload = {
      name: form.name.trim(),
      address1: form.address1.trim() || undefined,
      address2: form.address2.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      city: form.city.trim() || undefined,
      markagare: form.markagare.trim() || undefined,
      kontaktinfo: form.kontaktinfo.trim() || undefined,
      lat: parseLatLng(form.lat),
      lng: parseLatLng(form.lng),
      notes: form.notes.trim() || undefined,
    };

    setSaving(true);
    try {
      if (isNew) {
        const createRes = await fetch(`${API_BASE}/apiaries`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const createData = (await createRes.json().catch(() => ({}))) as { id?: number; error?: string };
        if (!createRes.ok || !createData.id) {
          throw new Error(createData.error || 'Kunde inte skapa bigård');
        }
        toast.success('Bigård skapad');
        navigate(`/apiaries/${createData.id}`, { replace: true });
        return;
      }

      const apiaryId = Number(id);
      const updateRes = await fetch(`${API_BASE}/apiaries/${apiaryId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const updateData = (await updateRes.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!updateRes.ok || !updateData.ok) {
        throw new Error(updateData.error || 'Kunde inte spara bigård');
      }

      await reloadDetail(apiaryId);
      setEditing(false);
      toast.success('Bigård uppdaterad');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara bigård');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!apiary || !canWrite('apiaries')) return;
    try {
      const res = await apiariesApi.remove(apiary.id);
      toast.success(res.message ?? 'Bigård uppdaterad');
      navigate('/apiaries');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort bigård');
    }
  }

  async function saveTodo() {
    if (!apiary || !canWrite('apiaries') || savingTodo) return;
    setSavingTodo(true);
    try {
      const res = await apiariesApi.update(apiary.id, { todoNotes: todoDraft.trim() });
      if (!res.ok) {
        throw new Error('Kunde inte spara att göra');
      }
      await reloadDetail(apiary.id);
      toast.success('Att göra sparad');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara att göra');
    } finally {
      setSavingTodo(false);
    }
  }

  async function handleCopyContact() {
    const value = apiary?.kontaktinfo?.trim() ?? '';
    if (!value) {
      toast.info('Ingen kontaktinfo att kopiera');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Kontaktinfo kopierad');
    } catch {
      toast.error('Kunde inte kopiera kontaktinfo');
    }
  }

  function handleCancelEdit() {
    if (isNew) {
      navigate('/apiaries');
      return;
    }
    setForm(apiaryToForm(apiary));
    setEditing(false);
  }

  const hasActiveHives = Number(apiary?.activeHiveCount ?? 0) > 0;
  const hasHistoricalHives = Number(apiary?.inactiveHiveCount ?? 0) > 0;
  const selectedLogType = apiaryLogForm.eventType.trim();
  const showTreatmentFields = apiaryLogForm.isTreatment || isTreatmentLogType(selectedLogType);

  useEffect(() => {
    let active = true;
    void hivesApi.meta()
      .then((meta) => {
        if (!active) return;
        setHiveLogTypeItems(meta.lists.HIVE_LOG_TYPES ?? []);
        setPreparations(meta.preparations ?? []);
      })
      .catch(() => {
        if (!active) return;
      });
    return () => {
      active = false;
    };
  }, []);

  async function saveApiaryLog() {
    if (!apiary || !canWriteHives || savingApiaryLog) return;
    if (!apiaryLogForm.eventType.trim()) {
      toast.error('Loggtyp krävs');
      return;
    }

    setSavingApiaryLog(true);
    try {
      const res = await apiariesApi.createHiveLogs(apiary.id, {
        logDate: apiaryLogForm.logDate ? `${apiaryLogForm.logDate} 12:00:00` : undefined,
        eventType: apiaryLogForm.eventType.trim(),
        isTreatment: showTreatmentFields,
        preparationProductId: showTreatmentFields && apiaryLogForm.preparationProductId ? Number(apiaryLogForm.preparationProductId) : undefined,
        preparation: showTreatmentFields ? apiaryLogForm.preparation.trim() || undefined : undefined,
        withdrawalDays: showTreatmentFields && apiaryLogForm.withdrawalDays ? Number(apiaryLogForm.withdrawalDays) : undefined,
        notes: apiaryLogForm.notes.trim() || undefined,
      });
      setApiaryLogForm(createEmptyApiaryLogForm());
      setShowApiaryLogDialog(false);
      toast.success(res.message ?? 'Kuploggar skapade');
      await reloadDetail(apiary.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa kuploggar');
    } finally {
      setSavingApiaryLog(false);
    }
  }

  function openApiaryLogDialog() {
    setApiaryLogForm(createEmptyApiaryLogForm());
    setShowApiaryLogDialog(true);
  }

  function closeApiaryLogDialog() {
    setShowApiaryLogDialog(false);
    setApiaryLogForm(createEmptyApiaryLogForm());
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Link to="/apiaries" className="text-sm text-primary hover:underline">
          Tillbaka
        </Link>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">{isNew ? 'Ny bigård' : apiary?.name || 'Bigård'}</h1>
          </div>
          {!isNew && !editing ? (
            <div className="flex items-center gap-2">
              {canWriteHives ? (
                <button type="button" className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={openApiaryLogDialog}>
                  Lägg till logg
                </button>
              ) : null}
              {canWrite('apiaries') ? (
                <button type="button" className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={() => setEditing(true)}>
                  Redigera
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="rounded border border-gray-200 bg-white p-3 text-sm text-gray-500">Laddar...</div>
      ) : editing ? (
        <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <input placeholder="Namn *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <input placeholder="Adressrad 1" value={form.address1} onChange={(e) => setForm((f) => ({ ...f, address1: e.target.value }))} />
            <input placeholder="Adressrad 2" value={form.address2} onChange={(e) => setForm((f) => ({ ...f, address2: e.target.value }))} />
            <input placeholder="Postnummer" value={form.postalCode} onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))} />
            <input placeholder="Stad" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
            <input placeholder="Markägare" value={form.markagare} onChange={(e) => setForm((f) => ({ ...f, markagare: e.target.value }))} />
            <input placeholder="Kontaktinformation" value={form.kontaktinfo} onChange={(e) => setForm((f) => ({ ...f, kontaktinfo: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Lat (t.ex. 57.12)" value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} />
              <input placeholder="Lng (t.ex. 15.34)" value={form.lng} onChange={(e) => setForm((f) => ({ ...f, lng: e.target.value }))} />
            </div>
            <textarea className="md:col-span-2" placeholder="Kommentar" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>

          <StaticMap
            lat={formLat}
            lng={formLng}
            editable
            onPick={(lat, lng) => setForm((f) => ({ ...f, lat: lat.toFixed(7), lng: lng.toFixed(7) }))}
          />

          <div className="flex items-center gap-2">
            {canWrite('apiaries') ? (
              <button type="button" onClick={() => void handleSave()} disabled={saving} className="rounded bg-success px-3 py-1.5 text-sm text-white hover:bg-success-hover disabled:opacity-50">
                {saving ? 'Sparar...' : 'Spara'}
              </button>
            ) : null}
            <button type="button" onClick={handleCancelEdit} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
              Avbryt
            </button>
            {!isNew && apiary && canWrite('apiaries') ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={hasActiveHives}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-danger hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                title={hasActiveHives ? 'Kan inte ta bort: bigården har aktiva kupor' : hasHistoricalHives ? 'Döljer bigården eftersom historiska kupor finns' : 'Tar bort bigården'}
              >
                {hasHistoricalHives ? 'Dölj' : 'Ta bort'}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-4 rounded border border-gray-200 bg-white p-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1 space-y-3">
              <InfoRow label="Fastighet" value={textValue(apiary?.name)} />
              <InfoRow label="Markägare" value={textValue(apiary?.markagare)} />
              <div className="grid grid-cols-[120px_1fr] gap-3 text-sm">
                <div className="font-medium text-gray-700">Kontaktinfo:</div>
                <div className="flex items-center gap-2 text-gray-900">
                  <span>{textValue(apiary?.kontaktinfo)}</span>
                  {apiary?.kontaktinfo?.trim() ? (
                    <button
                      type="button"
                      onClick={() => void handleCopyContact()}
                      className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                      title="Kopiera kontaktinfo"
                      aria-label="Kopiera kontaktinfo"
                    >
                      <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
                        <path d="M6 2a2 2 0 0 0-2 2v8h2V4h8V2H6Zm3 4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H9Zm0 2h7v8H9V8Z" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>
              <InfoRow label="Kommentar" value={textValue(apiary?.notes)} />
            </div>

            <div className="w-full shrink-0 lg:w-[300px]">
              <StaticMap lat={viewLat} lng={viewLng} editable={false} height={200} />
            </div>
          </div>

          <div className="rounded border border-gray-200 bg-white p-4">
            <div className="text-base font-medium text-gray-900">Att göra</div>
            <div className="mt-1 text-sm text-gray-500">Anteckningar för sådant du ska fixa eller ta med till bigården.</div>
            {canWrite('apiaries') ? (
              <div className="mt-3 space-y-3">
                <textarea
                  className="min-h-[120px] w-full"
                  placeholder="Skriv saker att komma ihåg för den här bigården"
                  value={todoDraft}
                  onChange={(e) => setTodoDraft(e.target.value)}
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="rounded-lg bg-success px-4 py-2 text-sm font-medium text-white hover:bg-success-hover disabled:opacity-50"
                    onClick={() => void saveTodo()}
                    disabled={savingTodo}
                  >
                    {savingTodo ? 'Sparar...' : 'Spara att göra'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 whitespace-pre-wrap text-sm font-medium text-gray-900">{textValue(apiary?.todoNotes)}</div>
            )}
          </div>

          {false ? (
            <div className="rounded border border-gray-200 bg-white p-4">
              <div className="text-base font-medium text-gray-900">Ny loggrad på alla aktiva kupor</div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr_2fr_auto]">
                <label className="text-sm text-gray-700">
                  Datum
                  <input className="mt-1 w-full" type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={apiaryLogForm.logDate} onChange={(e) => setApiaryLogForm((v) => ({ ...v, logDate: e.target.value }))} />
                </label>
                <label className="text-sm text-gray-700">
                  Typ
                  <select
                    className="mt-1 w-full"
                    value={apiaryLogForm.eventType}
                    onChange={(e) =>
                      setApiaryLogForm((v) => ({
                        ...v,
                        eventType: e.target.value,
                        isTreatment: isTreatmentLogType(e.target.value),
                      }))
                    }
                  >
                    <option value="">(välj loggtyp)</option>
                    {hiveLogTypeItems.map((item) => (
                      <option key={item.id} value={item.label}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-gray-700">
                  Kommentar
                  <input className="mt-1 w-full" value={apiaryLogForm.notes} onChange={(e) => setApiaryLogForm((v) => ({ ...v, notes: e.target.value }))} />
                </label>
                <div className="flex items-end">
                  <button type="button" className="w-full rounded-lg bg-success px-4 py-2 text-sm font-medium text-white hover:bg-success-hover disabled:opacity-50" onClick={() => void saveApiaryLog()} disabled={savingApiaryLog || hives.length === 0}>
                    {savingApiaryLog ? 'Sparar...' : 'Spara'}
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                {!isTreatmentLogType(selectedLogType) ? (
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={apiaryLogForm.isTreatment} onChange={(e) => setApiaryLogForm((v) => ({ ...v, isTreatment: e.target.checked }))} />
                    Detta är en behandling
                  </label>
                ) : null}
                {showTreatmentFields ? (
                  <>
                    <select
                      className="w-full"
                      value={apiaryLogForm.preparationProductId}
                      onChange={(e) => {
                        const selected = preparations.find((item) => item.id === Number(e.target.value));
                        setApiaryLogForm((v) => ({
                          ...v,
                          preparationProductId: e.target.value,
                          preparation: selected?.name ?? '',
                          withdrawalDays: selected?.withdrawalDays != null ? String(selected.withdrawalDays) : v.withdrawalDays,
                        }));
                      }}
                    >
                      <option value="">Välj preparat</option>
                      {preparations.map((preparation) => (
                        <option key={preparation.id} value={preparation.id}>
                          {preparation.sku ? `${preparation.sku} - ` : ''}
                          {preparation.name}
                        </option>
                      ))}
                    </select>
                    <input className="w-full" type="number" min={0} placeholder="Karenstid (dagar)" value={apiaryLogForm.withdrawalDays} onChange={(e) => setApiaryLogForm((v) => ({ ...v, withdrawalDays: e.target.value }))} />
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {showApiaryLogDialog ? (
            <div className="fixed inset-0 z-[1200] flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-6">
              <div className="relative z-[1210] max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">Ny loggrad på alla aktiva kupor</div>
                    <div className="text-sm text-gray-500">{apiary?.name || 'Bigård'}</div>
                  </div>
                  <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-lg text-gray-600 hover:bg-gray-50" onClick={closeApiaryLogDialog} aria-label="Stäng dialog">
                    ×
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr_2fr_auto]">
                  <label className="text-sm text-gray-700">
                    Datum
                    <input className="mt-1 w-full" type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={apiaryLogForm.logDate} onChange={(e) => setApiaryLogForm((v) => ({ ...v, logDate: e.target.value }))} />
                  </label>
                  <label className="text-sm text-gray-700">
                    Typ
                    <select
                      className="mt-1 w-full"
                      value={apiaryLogForm.eventType}
                      onChange={(e) =>
                        setApiaryLogForm((v) => ({
                          ...v,
                          eventType: e.target.value,
                          isTreatment: isTreatmentLogType(e.target.value),
                        }))
                      }
                    >
                      <option value="">(välj loggtyp)</option>
                      {hiveLogTypeItems.map((item) => (
                        <option key={item.id} value={item.label}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm text-gray-700">
                    Kommentar
                    <input className="mt-1 w-full" value={apiaryLogForm.notes} onChange={(e) => setApiaryLogForm((v) => ({ ...v, notes: e.target.value }))} />
                  </label>
                  <div className="flex items-end">
                    <button type="button" className="w-full rounded-lg bg-success px-4 py-2 text-sm font-medium text-white hover:bg-success-hover disabled:opacity-50" onClick={() => void saveApiaryLog()} disabled={savingApiaryLog || hives.length === 0}>
                      {savingApiaryLog ? 'Sparar...' : 'Spara'}
                    </button>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                  {!isTreatmentLogType(selectedLogType) ? (
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={apiaryLogForm.isTreatment} onChange={(e) => setApiaryLogForm((v) => ({ ...v, isTreatment: e.target.checked }))} />
                      Detta är en behandling
                    </label>
                  ) : null}
                  {showTreatmentFields ? (
                    <>
                      <select
                        className="w-full"
                        value={apiaryLogForm.preparationProductId}
                        onChange={(e) => {
                          const selected = preparations.find((item) => item.id === Number(e.target.value));
                          setApiaryLogForm((v) => ({
                            ...v,
                            preparationProductId: e.target.value,
                            preparation: selected?.name ?? '',
                            withdrawalDays: selected?.withdrawalDays != null ? String(selected.withdrawalDays) : v.withdrawalDays,
                          }));
                        }}
                      >
                        <option value="">Välj preparat</option>
                        {preparations.map((preparation) => (
                          <option key={preparation.id} value={preparation.id}>
                            {preparation.sku ? `${preparation.sku} - ` : ''}
                            {preparation.name}
                          </option>
                        ))}
                      </select>
                      <input className="w-full" type="number" min={0} placeholder="Karenstid (dagar)" value={apiaryLogForm.withdrawalDays} onChange={(e) => setApiaryLogForm((v) => ({ ...v, withdrawalDays: e.target.value }))} />
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <h2 className="text-base font-medium">Kupor på bigården</h2>
            <TableWrapper>
              <table className="w-full border-collapse text-xs">
                <TableHead>
                  <tr>
                    <th className="whitespace-nowrap px-1.5 py-1 text-left font-medium">Kupa</th>
                    <th className="whitespace-nowrap px-1.5 py-1 text-left font-medium">Fmt</th>
                    <th className="whitespace-nowrap px-1.5 py-1 text-left font-medium">YR</th>
                    <th className="whitespace-nowrap px-1.5 py-1 text-left font-medium">S</th>
                    <th className="whitespace-nowrap px-1.5 py-1 text-left font-medium">Dr</th>
                  </tr>
                </TableHead>
                <tbody>
                  {hives.length === 0 ? (
                    <tr>
                      <td className="whitespace-nowrap px-1.5 py-1.5 text-gray-500" colSpan={5}>
                        Inga kupor på bigården.
                      </td>
                    </tr>
                  ) : (
                    hives.map((hive) => (
                      <tr
                        key={hive.id}
                        className="cursor-pointer border-t border-gray-100 bg-white hover:bg-gray-50"
                        onClick={() => navigate(`/hives/${hive.id}`)}
                      >
                        <td className={`whitespace-nowrap px-1.5 py-1 ${isInQuarantine(hive.withdrawalUntil) ? 'bg-red-50' : ''}`}>
                          <button type="button" className="text-primary hover:underline" onClick={() => navigate(`/hives/${hive.id}`)}>
                            {hive.hiveNo != null ? String(hive.hiveNo) : hive.name}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-1.5 py-1">{shortFormatLabel(hive.hiveFormatLabel)}</td>
                        <td className="whitespace-nowrap px-1.5 py-1">
                          <div className="inline-flex items-center gap-0.5">
                            <BroodRoomIcon broodRoom={hive.broodRoomLabel} width={16} />
                            <span>{hive.broodRoomLabel || '-'}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-1.5 py-1">
                          <span>{splitStatusLabel(hive.hiveStatusLabel || hive.hiveStatus).short}</span>
                        </td>
                        <td className="whitespace-nowrap px-1.5 py-1">
                          <div className="inline-flex items-center gap-0.5">
                            <QueenColorIcon color={hive.queenColor} label={hive.queenColor || undefined} />
                            <span>{hive.queenStatusLabel || hive.queenStatus || '-'}</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableWrapper>
          </div>
        </div>
      )}
    </div>
  );
}
